import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { red, grey } from "@mui/material/colors";
import { notification } from "../../../utils/Notifications";
import {
  createUser,
  createSuperAdminUser,
  getUsers,
} from "../../../api/userApi";
import useLogout from "../../../hooks/auth/authHook.js";
import {
  Button,
  Container,
  Dialog,
  FormControl,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { TickCircle } from "iconsax-react";
import styles from "./adminFormDialogStyles.js";

AdminFormDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  fetchUsers: PropTypes.func.isRequired,
  zones: PropTypes.array,
};

export default function AdminFormDialog({
  onClose,
  open,
  fetchUsers,
  zones = [],
}) {
  const [logout] = useLogout();
  const { extraInformation } = useSelector((state) => state.user);
  const isSuperAdmin = extraInformation?.id_role === 5;

  const [containsRightLenght, setContainsRightLenght] = useState(false);
  const [containsLetter, setContainsLetter] = useState(false);
  const [containsNumbers, setContainsNumbers] = useState(false);
  const [containsSpecialCharacters, setContainsSpecialCharacters] =
    useState(false);

  const [name, setName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [selectedZone, setSelectedZone] = useState("");
  const [isZoneManager, setIsZoneManager] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const handleNameChange = (event) => {
    const value = event.target.value.slice(0, 20); // Limitar a 20 caracteres
    setName(value);
  };
  
  const handleMiddleNameChange = (event) => {
    const value = event.target.value.slice(0, 20); // Limitar a 20 caracteres
    setMiddleName(value);
  };
  
  const handleLastNameChange = (event) => {
    const value = event.target.value.slice(0, 20); // Limitar a 20 caracteres
    setLastName(value);
  };
  
  const handlePhoneChange = (event) => {
    // Solo permitir dígitos
    const value = event.target.value.replace(/\D/g, '');
    setPhoneNumber(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      phoneNumber: !validatePhoneNumber(value)
        ? "El teléfono debe tener 10 números"
        : "",
    }));
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      email: !validateEmail(event.target.value)
        ? "Correo electrónico no válido"
        : "",
    }));
  };

  const handlePasswordChange = (event) => setPassword(event.target.value);

  const handleZoneChange = (event) => setSelectedZone(event.target.value);

  const handleZoneManagerChange = (event) =>
    setIsZoneManager(event.target.checked);

  const validatePhoneNumber = (phoneNumber) => {
    const regex = /^\d{10}$/;
    return regex.test(phoneNumber);
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePassword = () => {
    const letterRegex = /[A-Za-z]/;
    const numberRegex = /[0-9]/;
    const specialCharRegex = /[!@#$%^&*]/;

    const containsRightLenght = password.length >= 8;
    const containsLetter = letterRegex.test(password);
    const containsNumbers = numberRegex.test(password);
    const containsSpecialCharacters = specialCharRegex.test(password);

    setContainsRightLenght(containsRightLenght);
    setContainsLetter(containsLetter);
    setContainsNumbers(containsNumbers);
    setContainsSpecialCharacters(containsSpecialCharacters);

    return (
      containsLetter &&
      containsNumbers &&
      containsSpecialCharacters &&
      containsRightLenght
    );
  };

  const clearFields = () => {
    setName("");
    setMiddleName("");
    setLastName("");
    setPhoneNumber("");
    setEmail("");
    setPassword("");
    setSelectedZone("");
    setIsZoneManager(false);
    setErrors({});
  };


  const handleCreateAdminClick = () => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    const user = {
      role: "admin",
      name,
      middleName,
      lastName,
      phoneNumber,
      email,
      password,
      permissions: selectedPermissions,
    };

    // Agregar campos según el rol
    if (isSuperAdmin) {
      // SuperAdmin: incluye is_zone_manager y available_zones
      user.is_zone_manager = isZoneManager;
      user.available_zones = selectedZone ? [Number(selectedZone)] : [];
    }

    // Seleccionar la función según el rol
    const createFunction = isSuperAdmin ? createSuperAdminUser : createUser;

    createFunction(user)
      .then((response) => {
        if (
          response.data.status_Code === 200 &&
          response.data.status_Message === "exist email"
        ) {
          notification({ text: "El correo ya esta en uso", type: "error" });
          setIsLoading(false);
          return;
        }

        if (response.data.status_Code === 201) {
          notification({ text: "Administrador creado", type: "success" });
          getUsers()
            .then((response) => {
              if (response.data.status_Code === 200) {
                fetchUsers();
                onClose();
              } else if (response.data.status_Code === 401) {
                notification({
                  text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
                  type: "error",
                });
                logout();
                onClose();
              } else {
                notification({
                  text: "Error al obtener los usuarios",
                  type: "error",
                });
                onClose();
              }
            })
            .catch(() => {
              notification({
                text: "Error de conexión, revisa tu conexión a internet",
                type: "error",
              });
              onClose();
            })
            .finally(() => {
              clearFields();
              setIsLoading(false);
            });
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al crear el administrador",
            type: "error",
          });
          setIsLoading(false);
        }
      })
      .catch(() => {
        setIsLoading(false);
        clearFields();
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      });
  };

  useEffect(() => {
    const validateForm = () => {
      // Validar campos básicos
      const basicFieldsValid =
        name && middleName && lastName && phoneNumber && email && password;

      // Validar zona solo si es SuperAdmin
      const zoneValid = isSuperAdmin ? selectedZone : true;

      const hasNoErrors =
        !errors.email && !errors.phoneNumber && validatePassword();

      setIsFormValid(basicFieldsValid && zoneValid && hasNoErrors);
    };

    validateForm();
  }, [
    name,
    middleName,
    lastName,
    phoneNumber,
    email,
    password,
    errors,
    selectedPermissions,
    selectedZone,
    isSuperAdmin,
  ]);

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      onClose={handleDialogClose}
      open={open}
      PaperProps={{ style: { borderRadius: "16px" } }}
    >
      <Container style={{ padding: 26 }}>
        <Typography
          component="div"
          variant="h5"
          style={{
            textAlign: "center",
            fontWeight: "bold",
            color: grey[800],
            marginBottom: 26,
          }}
        >
          Nuevo administrador
        </Typography>

        <FormControl variant="standard">
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="standard-basic"
                label="* Nombre"
                variant="standard"
                value={name}
                onChange={handleNameChange}
                error={!!errors.name}
                helperText={errors.name}
                style={{ width: "100%" }}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="standard-basic"
                label="* Apellido paterno"
                variant="standard"
                value={middleName}
                onChange={handleMiddleNameChange}
                error={!!errors.middleName}
                helperText={errors.middleName}
                style={{ width: "100%" }}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="standard-basic"
                label="* Apellido materno"
                variant="standard"
                value={lastName}
                onChange={handleLastNameChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                style={{ width: "100%" }}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="standard-basic"
                label="* Teléfono"
                variant="standard"
                value={phoneNumber}
                onChange={handlePhoneChange}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                style={{ width: "100%" }}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={8}>
              <TextField
                id="standard-basic"
                label="* Correo electrónico"
                variant="standard"
                value={email}
                onChange={handleEmailChange}
                error={!!errors.email}
                helperText={errors.email}
                style={{ width: "100%" }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={isSuperAdmin ? 4 : 12}
              md={isSuperAdmin ? 4 : 12}
            >
              <TextField
                id="standard-basic"
                label="* Contraseña"
                variant="standard"
                value={password}
                onChange={handlePasswordChange}
                error={!!errors.password}
                helperText={errors.password}
                style={{ width: "100%" }}
              />
            </Grid>
            {isSuperAdmin && (
              <>
                <Grid item xs={12} sm={4} md={4}>
                  <FormControl variant="standard" fullWidth>
                    <InputLabel id="zone-select-label">* Zona</InputLabel>
                    <Select
                      labelId="zone-select-label"
                      id="zone-select"
                      value={selectedZone}
                      onChange={handleZoneChange}
                    >
                      {zones.map((zone) => {
                        // Manejar tanto strings como objetos
                        const zoneId =
                          typeof zone === "string"
                            ? zone
                            : zone.id_zone ??
                              zone.zone_id ??
                              zone.id ??
                              zone.zone_code ??
                              zone.name ??
                              zone.zone_name;
                        const zoneLabel =
                          typeof zone === "string"
                            ? zone
                            : zone.zone_name ??
                              zone.name ??
                              zone.zone_code ??
                              zoneId;
                        return (
                          <MenuItem key={zoneId} value={zoneId}>
                            {zoneLabel}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <FormControlLabel
                    sx={styles.regionalSwitch}
                    control={
                      <Switch
                        checked={isZoneManager}
                        onChange={handleZoneManagerChange}
                      />
                    }
                    label="Regional"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </FormControl>

        <Container style={styles.rulesContainer}>
          <Container style={styles.ruleContainer}>
            <TickCircle
              size="18"
              style={
                containsRightLenght
                  ? styles.successRuleIcon
                  : styles.errorRuleIcon
              }
              variant="Bold"
            />
            <Typography variant="body2">
              Contiene 8 caracteres como mínimo
            </Typography>
          </Container>
          <Container style={styles.ruleContainer}>
            <TickCircle
              size="18"
              style={
                containsLetter ? styles.successRuleIcon : styles.errorRuleIcon
              }
              variant="Bold"
            />
            <Typography variant="body2">Contiene letras</Typography>
          </Container>
          <Container style={styles.ruleContainer}>
            <TickCircle
              size="18"
              style={
                containsNumbers ? styles.successRuleIcon : styles.errorRuleIcon
              }
              variant="Bold"
            />
            <Typography variant="body2">Contiene números</Typography>
          </Container>
          <Container style={styles.ruleContainer}>
            <TickCircle
              size="18"
              style={
                containsSpecialCharacters
                  ? styles.successRuleIcon
                  : styles.errorRuleIcon
              }
              variant="Bold"
            />
            <Typography variant="body2">
              Contiene caracteres especiales
            </Typography>
          </Container>
        </Container>

        {/* <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Selecciona permisos que tendrá el administrador:</Typography>
          <FormGroup>
            {permissions.map((perm) => (
              <FormControlLabel
                key={perm.permission_id}
                control={
                  <Checkbox
                    checked={selectedPermissions.includes(perm.permission_id)}
                    onChange={() => handlePermissionChange(perm.permission_id)}
                  />
                }
                label={perm.permission_description}
              />
            ))}
          </FormGroup>
        </Box> */}

        <Container
          style={{
            display: "flex",
            justifyContent: "end",
            width: "100%",
            padding: 0,
            marginTop: 36,
            gap: 12,
          }}
        >
          <Button
            variant="standard"
            color="primary"
            onClick={handleDialogClose}
            style={{
              width: "120px",
              borderRadius: "0.5rem",
              height: "38px",
              color: red["A400"],
              backgroundColor: red["50"],
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!isFormValid}
            onClick={handleCreateAdminClick}
            sx={isFormValid ? styles.activeButton : styles.inactiveButton}
          >
            {isLoading ? (
              <CircularProgress size={24} style={{ color: "white" }} />
            ) : (
              "Crear"
            )}
          </Button>
        </Container>
      </Container>
    </Dialog>
  );
}
